import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import {
  BonusQuote,
  BonusQuoteReason,
  BonusTier,
  MICROS_PER_UNIT,
  TOO_MANY_DECIMALS_PATTERN,
  WalletBilling,
  WalletPrices,
  bonusQuoteReasonLabel,
  microsToDecimalString,
  unitsToMicros,
} from 'src/app/models/Wallet';
import {
  MONEY_BALANCE_FRACTION_DIGITS,
  MONEY_DEFAULT_LOCALE,
  MONEY_MAX_FRACTION_DIGITS,
} from 'src/app/shared/pipes/money-micros.pipe';
import {
  AI_COST_SAMPLE_LIMIT,
  AiCostRange,
  RechargeYieldRow,
  aiCostRangeFromEntries,
  aiCostWindowStartIso,
  isBilledByWallet,
  rechargeYieldRows,
} from './recharge-yield';
import { RechargesService } from 'src/app/shared/services/recharges.service';
import { WalletService } from 'src/app/shared/services/wallet.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { AccountVersionService } from 'src/app/shared/services/account-version.service';

/**
 * Espera entre a digitação e a cotação. Curto o bastante para o resumo parecer
 * ao vivo e longo o bastante para "2500" não virar quatro requisições.
 */
export const BONUS_QUOTE_DEBOUNCE_MS = 300;

/**
 * Total da cotação de abertura, só para trazer `tiers` e `min_total_micros`.
 *
 * As faixas dos chips vêm da MESMA rota que cota o bônus, e na abertura ainda
 * não há valor digitado — então a primeira chamada pergunta por zero. A resposta
 * dela vale pelas faixas e pelo mínimo; `bonus_micros` e `reason` só passam a
 * valer quando o cliente escolhe um valor.
 */
export const BONUS_QUOTE_BOOTSTRAP_TOTAL = '0.00';

/** Só o PIX: é a única forma que o `POST /recharges` por valor aceita hoje. */
export const RECHARGE_PAYMENT_TYPE = 'pix';

/**
 * A carteira é única, mas a recarga ainda grava o rótulo do produto. `sms` é o
 * mesmo padrão da compra por quantidade (`recharge.component`).
 */
export const RECHARGE_DEFAULT_TARGET = 'sms';

/** Abaixo disto (R$ 0,00005) um custo de IA arredonda para zero em 4 casas. */
export const AI_COST_ROUNDS_TO_ZERO_BELOW_MICROS = 50;

/**
 * Motivos que dizem que a CONTA não recebe bônus, seja qual for o valor. Com
 * eles os chips não podem prometer `+R$ 10,00 de bônus` — o cliente escolheria
 * a faixa e veria zero no resumo sem entender por quê.
 */
export const ACCOUNT_BONUS_EXCLUSION_REASONS: BonusQuoteReason[] = [
  'subscriber_excluded',
  'account_price_excluded',
  'not_in_wallet_cohort',
];

/** Passo do modal: escolher o valor, ou pagar o PIX que já foi gerado. */
export type RechargeStep = 'amount' | 'pix';

/**
 * Recarga por VALOR da coorte da carteira (CU-86akn0zp6).
 *
 * Mora em `pages/wallet/` porque é carteira, mas ganha módulo próprio
 * (`RechargeModalModule`) porque o painel — que é eager — também a abre. Pôr o
 * componente no `WalletModule` obrigaria o painel a carregar o módulo lazy da
 * carteira inteiro; pôr no `SharedModule` a empurraria para os outros quinze
 * módulos que importam o compartilhado e não têm nada com recarga.
 *
 * ## Por que a cotação é do servidor, e a cada valor
 *
 * O bônus é dinheiro creditado. Calcular a faixa no painel daria um número que
 * o cliente lê antes de pagar e que o backend pode não repetir — e a diferença
 * apareceria só depois do PIX pago. Então as faixas, o mínimo e o bônus saem
 * todos de `GET /recharges/bonus-quote`, e o painel só desenha.
 *
 * ## Abertura como `Subject`
 *
 * Mesmo molde do `app-wallet-modal`: o modal mora no
 * template do dono e a abertura é um valor empurrado pelo pai. É o que permite
 * o painel e a tela da carteira abrirem o mesmo componente sem conhecer o
 * `NgbModal`.
 */
@Component({
  selector: 'app-recharge-modal',
  templateUrl: './recharge-modal.component.html',
  styleUrls: ['./recharge-modal.component.scss'],
})
export class RechargeModalComponent implements OnInit, OnDestroy {

  @Input() showModal = new Subject<string>();

  /** Compra registrada — o dono recarrega saldo e extrato. */
  @Output() completed = new EventEmitter<void>();

  @ViewChild('rechargeModal') modal: any;

  modalRef: NgbModalRef;

  step: RechargeStep = 'amount';

  /** Valor digitado, em reais. `null` é campo vazio, que não é zero. */
  amount: number = null;

  quote: BonusQuote = null;
  quoting = false;
  quoteFailed = false;

  /** Faixas e mínimo da cotação de abertura; não mudam enquanto o modal vive. */
  tiers: BonusTier[] = [];
  minTotalMicros = 0;

  /**
   * Motivo, já em texto, de a conta não receber bônus em faixa nenhuma. Sai de
   * uma cotação no piso da primeira faixa logo na abertura: a cotação de zero
   * responde `no_tier` antes de olhar contrato e assinatura.
   */
  accountBonusExclusion: string = null;

  /**
   * A conta recebe bônus de recarga? (CU-17tm466tzkz, `bonus_eligible` da cotação).
   * `false` some com tudo que fala de bônus: chips de faixa (o backend já os manda
   * vazios), a linha "Bônus" do resumo e os avisos — a conta não tem bônus a
   * explicar, e mostrar "Bônus R$ 0,00" a quem negociou contrato só gera pergunta.
   */
  bonusEligible = true;

  /** Saldo disponível da carteira hoje. `null` é saldo não carregado. */
  availableMicros: number = null;

  /**
   * Preço efetivo de cada produto para ESTA conta (contrato → assinante → padrão), como
   * `GET /wallet/prices` devolve. `null` é leitura que falhou: a tabela some, a recarga segue.
   */
  prices: WalletPrices = null;

  /** De onde sai cada canal (`GET /wallet/billing`). `null` → todos como carteira. */
  billing: WalletBilling = null;

  /**
   * Faixa do que cada resposta de IA custou nesta conta (débitos reais da carteira, últimos
   * 30 dias). `null` é "sem dado suficiente" — a tela fica só com o texto, sem número.
   */
  aiCostRange: AiCostRange = null;

  buying = false;

  pixImage: string = null;
  pixCode: string = null;

  readonly paymentType = RECHARGE_PAYMENT_TYPE;
  readonly balanceLocale = MONEY_DEFAULT_LOCALE;
  readonly balanceFractionDigits = MONEY_BALANCE_FRACTION_DIGITS;
  /**
   * Teto de casas do custo por resposta de IA: 4. Uma resposta custa frações de centavo
   * (~R$ 0,0007), e com as 3 casas do extrato a faixa poderia sair "entre R$ 0,000 e
   * R$ 0,001". O mínimo segue o piso do pipe (2). O preço unitário de SMS/WhatsApp/ligação
   * usa o padrão do pipe (2 a 6 casas), o mesmo da linha "Seu preço do WhatsApp" de antes.
   *
   * Exceção: se o piso da faixa for menor que meio décimo de milésimo (< 50 micros), em 4
   * casas ele sairia "R$ 0,00" — uma resposta que tirou saldo aparecendo como de graça.
   * Aí a faixa usa as 6 casas dos micros, e nenhum valor dela vira zero.
   */
  get aiCostMaxFractionDigits(): number {
    const low = this.aiCostRange ? this.aiCostRange.lowMicros : null;

    return low !== null && low < AI_COST_ROUNDS_TO_ZERO_BELOW_MICROS ? MONEY_MAX_FRACTION_DIGITS : 4;
  }

  /** Cada `next` vira uma cotação — e cancela a anterior. */
  private readonly amountChanges = new Subject<number>();

  private subscriptions: Subscription[] = [];

  /**
   * O que uma abertura pede para montar a tela (cotação ao vivo, faixas, saldo, preço). Reabrir
   * cancela a da abertura anterior: o `distinctUntilChanged` da cotação lembrava o valor da
   * abertura anterior — escolher de novo R$ 100 não cotava, e o resumo saía sem o bônus da faixa.
   * A compra fica de fora (ver `buy`).
   */
  private openingSubscriptions = new Subscription();

  /**
   * Reais para micros. `null`, valor inválido e valor com mais de 2 casas são zero, não NaN: com
   * 200.555 o resumo mostraria R$ 200,56 e o `buy()` mandaria "200.555" — o PIX sairia por um valor
   * diferente do mostrado. Zero não cota nem libera a compra; o campo diz por quê.
   */
  get amountMicros(): number {
    const value = Number(this.amount);

    return isFinite(value) && value > 0 && !this.amountTooManyDecimals ? unitsToMicros(value) : 0;
  }

  /** O `type="number" step="0.01"` não recusa 3 casas: quem recusa é a tela. */
  get amountTooManyDecimals(): boolean {
    return this.amount !== null && TOO_MANY_DECIMALS_PATTERN.test(String(this.amount));
  }

  get hasAmount(): boolean {
    return this.amountMicros > 0;
  }

  /**
   * Abaixo do mínimo o botão fica travado. O erro só aparece depois de haver
   * valor: campo recém-aberto não é campo errado.
   */
  get isBelowMinimum(): boolean {
    return this.hasAmount && this.minTotalMicros > 0 && this.amountMicros < this.minTotalMicros;
  }

  get canBuy(): boolean {
    return this.hasAmount && !this.isBelowMinimum && !this.buying;
  }

  /** Bônus desta cotação, e só quando ela é do valor que está na tela. */
  get bonusMicros(): number {
    return this.isQuoteCurrent ? this.quote.bonus_micros || 0 : 0;
  }

  get creditedMicros(): number {
    return this.amountMicros + this.bonusMicros;
  }

  /**
   * Motivo da exclusão em texto. `ok` e `no_tier` não têm o que explicar — o
   * resumo já mostra bônus zerado e os chips já dizem a partir de quanto.
   */
  get exclusionReason(): string {
    return this.isQuoteCurrent && this.bonusEligible ? bonusQuoteReasonLabel(this.quote.reason) : null;
  }

  get minimumLabelMicros(): number {
    return this.minTotalMicros;
  }

  /** O aviso da abertura não se repete quando o resumo já diz a mesma coisa. */
  get showAccountBonusExclusion(): boolean {
    return !!this.accountBonusExclusion && this.exclusionReason !== this.accountBonusExclusion;
  }

  /** Saldo depois da recarga: o de hoje, mais o pago, mais o bônus cotado. */
  get projectedBalanceMicros(): number {
    const available = Math.max(this.availableMicros || 0, 0);
    const credited = this.hasAmount && !this.isBelowMinimum ? this.creditedMicros : 0;

    return available + credited;
  }

  /**
   * O saldo que a tabela divide: o projetado (hoje + recarga + bônus cotado), ou `null`
   * quando o saldo de hoje não carregou — aí a tabela mostra preço, mas não quantidade.
   */
  get yieldBalanceMicros(): number | null {
    return this.availableMicros === null ? null : this.projectedBalanceMicros;
  }

  /**
   * Uma linha por produto cobrado em unidade (SMS, WhatsApp, ligação), já com o preço do
   * cliente e o quanto rende. A conta é de `rechargeYieldRows` — o template só desenha.
   * Objetos novos a cada leitura: no `*ngFor` use `trackBy: trackYieldRow`.
   */
  get yieldRows(): RechargeYieldRow[] {
    return rechargeYieldRows(this.prices, this.billing, this.yieldBalanceMicros);
  }

  get hasYieldTable(): boolean {
    return this.yieldRows.length > 0;
  }

  /** IA sai deste saldo? Com `billing.ai === 'credits'` ela é do pacote de tokens. */
  get aiBilledByWallet(): boolean {
    return isBilledByWallet(this.billing, 'ai');
  }

  trackYieldRow(_index: number, row: RechargeYieldRow): string {
    return row.service;
  }

  /** Milhar com ponto, como o resto da tela. `null` não chega aqui (o template testa antes). */
  formatQuantity(quantity: number): string {
    return quantity.toLocaleString(this.balanceLocale);
  }

  constructor(
    private modalService: NgbModal,
    private rechargesService: RechargesService,
    private walletService: WalletService,
    private toastService: ToastService,
    private accountVersionService: AccountVersionService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.showModal.subscribe(() => this.open())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.openingSubscriptions.unsubscribe();
    this.amountChanges.complete();
  }

  open(): void {
    this.openingSubscriptions.unsubscribe();
    this.openingSubscriptions = new Subscription();
    this.reset();
    this.openingSubscriptions.add(this.watchAmountChanges());
    // A classe de versão vive no `<section>` do shell, e o `NgbModal` pendura a
    // janela no `body` — fora dele. Sem levar a classe junto, os tokens do modal
    // caem no `:root` e o botão primário fica verde no escopo internacional.
    this.modalRef = this.modalService.open(this.modal, {
      size: 'lg',
      windowClass: this.accountVersionService.themeClass,
      beforeDismiss: () => !this.buying,
    });
    this.loadTiers();
    this.loadYieldBase();
  }

  close(): void {
    // Com a compra em voo o modal fica aberto até o QR chegar (ver `buy`).
    if (this.modalRef && !this.buying) {
      this.modalRef.dismiss('CLOSE');
      this.modalRef = null;
    }
  }

  /** Chip: escolhe o piso da faixa, que é o valor que a destrava. */
  selectTier(tier: BonusTier): void {
    this.amount = tier.min_total_micros / MICROS_PER_UNIT;
    this.onAmountChanged();
  }

  isTierSelected(tier: BonusTier): boolean {
    return this.amountMicros === tier.min_total_micros;
  }

  onAmountChanged(): void {
    this.amountChanges.next(this.amountMicros);
  }

  /**
   * Compra.
   *
   * Mesmo desfecho da compra por quantidade (ver `RechargeComponent.registerBuy`):
   * aviso de que o crédito pode não estar confirmado e QR + copia-e-cola na
   * tela. O que NÃO se repete aqui é o `router.navigate(['/recharges'])` — a
   * navegação fecharia o modal em cima do QR que ela mesma acabou de pedir.
   *
   * A compra em voo NÃO é cancelada ao reabrir nem ao destruir: abortar o POST não desfaz a
   * recarga PIX que o servidor já pode ter criado, e sem a resposta o cliente não vê o QR e
   * compra de novo. Enquanto ela voa o modal não fecha (`beforeDismiss`) e, se for reaberto por
   * outro caminho, `buying` segue ligado e o QR cai no modal aberto.
   */
  buy(): void {
    if (!this.canBuy) {
      return;
    }

    this.buying = true;

    this.rechargesService
      .buyByValue({
        total: microsToDecimalString(this.amountMicros),
        payment_type: this.paymentType,
        target: RECHARGE_DEFAULT_TARGET,
      })
      .subscribe({
        next: (response) => {
          this.buying = false;

          let message = 'Recarga registrada com sucesso.';

          if (!response?.confirmated_at) {
            message += ' O saldo entra assim que o pagamento for confirmado.';
          }

          this.toastService.success(message);

          this.pixImage = response?.url_qrcode_pix || null;
          this.pixCode = response?.pix_code || null;
          this.step = 'pix';

          this.completed.emit();
        },
        error: (response) => {
          this.buying = false;

          if (response?.error?.message) {
            this.toastService.error(response.error.message);
          }
        },
      });
  }

  pixCopy(): void {
    if (!this.pixCode) {
      return;
    }

    navigator.clipboard.writeText(this.pixCode);
    this.toastService.success('Código PIX copiado para a área de transferência!');
  }

  /** A cotação na mão descreve o valor que está na tela agora. */
  private get isQuoteCurrent(): boolean {
    return !!this.quote && this.quote.paid_micros === this.amountMicros;
  }

  private reset(): void {
    this.step = 'amount';
    this.amount = null;
    this.quote = null;
    this.quoting = false;
    this.quoteFailed = false;
    this.tiers = [];
    this.minTotalMicros = 0;
    this.accountBonusExclusion = null;
    this.bonusEligible = true;
    this.availableMicros = null;
    this.prices = null;
    this.billing = null;
    this.aiCostRange = null;
    // `buying` fica: uma compra ainda em voo segue valendo para o modal reaberto.
    this.pixImage = null;
    this.pixCode = null;
  }

  /**
   * Cotação de abertura: vale pelas faixas e pelo mínimo, não pelo bônus.
   *
   * Se ela falhar, o modal segue funcionando sem chips e sem mínimo — a
   * validação do valor passa a ser do backend, o que é pior do que validar
   * antes, mas melhor do que um modal que não abre.
   */
  private loadTiers(): void {
    this.openingSubscriptions.add(
      this.rechargesService.bonusQuote(BONUS_QUOTE_BOOTSTRAP_TOTAL).subscribe({
        next: (quote) => {
          this.bonusEligible = quote?.bonus_eligible !== false;
          this.tiers = this.bonusEligible ? quote.tiers || [] : [];
          this.minTotalMicros = quote.min_total_micros || 0;
          this.checkAccountBonusEligibility();
        },
        error: () => {
          this.tiers = [];
          this.minTotalMicros = 0;
        },
      })
    );
  }

  /**
   * Cota o piso da primeira faixa só para saber se a CONTA recebe bônus.
   *
   * A regra é do backend (contrato exclui o bônus enquanto
   * `WALLET_BONUS_WITH_ACCOUNT_PRICE` estiver desligada; assinante idem com a
   * flag dele) e aqui só se lê o motivo. Falha não vira aviso: o resumo ao vivo
   * explica do mesmo jeito quando o cliente escolhe um valor.
   */
  private checkAccountBonusEligibility(): void {
    if (this.tiers.length === 0) {
      return;
    }

    this.openingSubscriptions.add(
      this.rechargesService.bonusQuote(microsToDecimalString(this.tiers[0].min_total_micros)).subscribe({
        next: (quote) => {
          this.accountBonusExclusion = ACCOUNT_BONUS_EXCLUSION_REASONS.includes(quote?.reason)
            ? bonusQuoteReasonLabel(quote.reason)
            : null;
        },
        error: () => {
          this.accountBonusExclusion = null;
        },
      })
    );
  }

  /**
   * O que a tabela "quanto rende" precisa: saldo de hoje, preço de cada produto deste
   * cliente, de onde sai cada canal e o histórico de custo da IA.
   *
   * O preço vem de `GET /wallet/prices`, que já resolve a cascata (contrato →
   * assinante → padrão) — calcular aqui daria um número diferente do que a
   * carteira cobra. Qualquer falha só esconde o pedaço dela: a recarga segue.
   */
  private loadYieldBase(): void {
    this.openingSubscriptions.add(
      this.walletService.getBalance().subscribe({
        next: (balance) => {
          this.availableMicros = balance?.available_micros ?? 0;
        },
        error: () => {
          this.availableMicros = null;
        },
      })
    );

    this.openingSubscriptions.add(
      this.walletService.getPrices().subscribe({
        next: (prices) => {
          this.prices = prices || null;
        },
        error: () => {
          this.prices = null;
        },
      })
    );

    // Sem billing a tabela trata todo canal como carteira — a mesma escolha de "Sua tarifa".
    this.openingSubscriptions.add(
      this.walletService.getBilling().subscribe({
        next: (billing) => {
          this.billing = billing || null;
        },
        error: () => {
          this.billing = null;
        },
      })
    );

    // Uma página do extrato: os débitos de IA mais recentes da janela. Falha = sem faixa.
    this.openingSubscriptions.add(
      this.walletService.listEntries({
        service: 'ai',
        entry_type: 'debit',
        from: aiCostWindowStartIso(),
        limit: AI_COST_SAMPLE_LIMIT,
      }).subscribe({
        next: (page) => {
          this.aiCostRange = aiCostRangeFromEntries(page?.data || []);
        },
        error: () => {
          this.aiCostRange = null;
        },
      })
    );
  }

  private watchAmountChanges(): Subscription {
    return this.amountChanges
      .pipe(
        debounceTime(BONUS_QUOTE_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap((micros) => {
          // Abaixo do mínimo não existe compra, então não há bônus a cotar — e
          // a requisição que sairia daqui voltaria 422 na cara do cliente
          // enquanto ele ainda está digitando. Sem mínimo conhecido (a cotação
          // de abertura falhou) a cotação sai mesmo assim: aí quem valida é o
          // backend, o que é pior do que validar antes, mas melhor do que um
          // resumo que nunca aparece.
          const belowMinimum = this.minTotalMicros > 0 && micros < this.minTotalMicros;

          if (micros <= 0 || belowMinimum) {
            this.quoting = false;
            this.quoteFailed = false;
            return of(null as BonusQuote);
          }

          this.quoting = true;
          this.quoteFailed = false;

          // O erro é tratado DENTRO do inner observable: deixá-lo subir
          // encerraria o pipeline e o resumo pararia de reagir à digitação
          // depois da primeira falha.
          return this.rechargesService
            .bonusQuote(microsToDecimalString(micros))
            .pipe(catchError(() => of(null as BonusQuote)));
        })
      )
      .subscribe((quote) => {
        this.quoteFailed = this.quoting && quote === null;
        this.quoting = false;
        this.quote = quote;

        // O mínimo pode chegar aqui primeiro quando a cotação de abertura
        // demora mais do que o cliente leva para digitar.
        if (quote) {
          if (quote.bonus_eligible === false) {
            this.bonusEligible = false;
          }
          this.tiers = !this.bonusEligible ? [] : (quote.tiers?.length ? quote.tiers : this.tiers);
          this.minTotalMicros = quote.min_total_micros || this.minTotalMicros;
        }
      });
  }
}
